<template>
  <div>
    <div class="page-header" v-if="!shouldShowGetingImage && !showNotFoundPage">
      <PageTitle
        back-to-route="home"
        :data="configuration"
        override-title="details-view-title"
      />

      <div class="page-header-actions action-buttons">
        <AlButton
          variant="ghost"
          v-on:click="editConfiguration"
          v-featureflow:isOn="'capacity-planner-edit'"
          id="edit-configuration"
        >
          <Settings />
        </AlButton>
        <AlButton
          variant="ghost"
          v-on:click="downloadMonthlyCsv"
          :disabled="generatingCsv"
          v-featureflow:isOn="'capacity-planner-download-csv'"
          id="download-csv"
        >
          <AlIcon icon="download" v-if="!generatingCsv" />
          <AlLoading v-if="generatingCsv" size="sm" />
        </AlButton>
      </div>
    </div>

    <div>
      <NotFound v-if="showNotFoundPage" />
    </div>
    <div class="img-wrapper" v-if="shouldShowGetingImage && !showNotFoundPage">
      <img
        src="../assets/playvox-loader.gif"
        alt="Wait monthly summary is loading"
      />
      <div>
        We are preparing your capacity plan.
        <br />
        Grab a cup of tea as it might take a few minutes.
      </div>
    </div>
    <div id="details-tabs" v-if="!shouldShowGetingImage && !showNotFoundPage">
      <AlTabs
        :tabs="tabs"
        @tab-active="selectTab"
        v-featureflow:isOn="'capacity-planner-staffing'"
      />
      <router-view
        class="monthly-tab-body"
        @change="onChange"
        :configuration="configuration"
        :months="months"
        :warnings="warnings"
        :workloadTasks="workloadTasks"
        :showRequiredUnavailable="showRequiredUnavailable"
      ></router-view>
    </div>
    <ErrorModal
      :showModal="
        !noForecastRequirementWarningModalActive &&
        !noAgentGotShiftWarningModalActive &&
        isErrorModal
      "
      @continue-modal="navigateToListPage"
      @cancel-modal="navigateToListPage"
    />
    <AlModal
      :config="noForecastRequirementWarningModalConfig"
      :open-modal="noForecastRequirementWarningModalActive"
      @close="closeNoForecastRequirementWarningModal()"
    >
      <span>
        <Localization
          message="monthly-summary-no-forecast-requirements-message"
        />
      </span>
    </AlModal>
    <AlModal
      :config="noAgentGotShiftWarningModalConfig"
      :open-modal="noAgentGotShiftWarningModalActive"
      @close="closeNoAgentGotShiftWarningModal()"
    >
      <span>
        <Localization message="monthly-summary-no-agent-got-shift-message" />
      </span>
    </AlModal>
  </div>
</template>

<script>
import {
  AlTabs,
  Al2Button as AlButton,
  AlIcon,
  AlLoading,
  AlModal,
} from "@playvox/almendra-components";
import { eachMonthOfInterval, isSameMonth, parseISO } from "date-fns";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { detailsPageEventBus } from "../config/eventbus";

import {
  Translator,
  PageTitle,
  Icons,
  CapacityPlannerConfiguration,
  CapacityPlannerMonthlyView,
  CapacityPlannerWarning,
  exportToCsv,
  formatNumberForCsv,
  RosterConfiguration,
  Forecast,
  WorkloadTasks,
  NotFound,
  Localization,
} from "@agyletime/wfm-vue-common";
const { Settings } = Icons;
import ErrorModal from "./formParts/ErrorModal.vue";

const MONTHLY_SUMMARY_POLLING_INTERVAL_REQUIRED = 5000;
const MONTHLY_SUMMARY_POLLING_INTERVAL_AVAILABLE = 30000;
const MAX_RETRY_CALL = 40;
const HTTP_STATUS_403 = 403;
export default {
  name: "DetailsTabs",
  components: {
    AlTabs,
    AlButton,
    PageTitle,
    AlIcon,
    Settings,
    AlLoading,
    NotFound,
    ErrorModal,
    AlModal,
    Localization,
  },
  mixins: [Translator],
  data() {
    return {
      tabs: [],
      configuration: {},
      months: [],
      warnings: [],
      workloadTasks: new Map(),
      generatingCsv: false,
      bus: detailsPageEventBus,
      pollingRequiredStopCount: 0,
      pollingAvailableStopCount: 0,
      showNotFoundPage: false,
      isErrorModal: false,
      showRequiredUnavailable: false,
      disableOpenNoForecastRequirementWarningModal: false,
      noForecastRequirementWarningModalActive: false,
      noForecastRequirementWarningModalConfig: {
        header: "No Forecast Staffing Requirements",
      },
      disableOpenNoAgentGotShiftModal: false,
      noAgentGotShiftWarningModalActive: false,
      noAgentGotShiftWarningModalConfig: {
        header: "User availability does not match with roster configuration",
      },
    };
  },
  async created() {
    this.getWorkloadTasks();

    this.tabs = [
      {
        label: this.translate("capacity-planner-breadcrumb-details-view"),
        name: "details-view",
        to: "/view/:id/details",
        visible: this.featureEnabled("capacity-planner-staffing"),
      },
      {
        label: this.translate("capacity-planner-breadcrumb-details-staffing"),
        name: "staffing-view",
        to: "/view/:id/staffing",
        visible: this.featureEnabled("capacity-planner-staffing"),
      },
      {
        label: "Schedule",
        name: "schedule-view",
        to: "/view/:id/schedule",
        visible: this.featureEnabled("capacity-planner-schedule-tab"),
      },
    ];

    const id = this.$route.params.id;
    this.bus.$on("adjustmentChanged", this.retrieveMonthsAndWarnings);
    if (id) {
      this.loading = true;
      // Start polling for monthly summary
      await this.retrieveMonthlySummary(id);
    }
    this.loading = false;

    if (!this.featureEnabled("capacity-planner-staffing")) {
      const matches = this.$router.currentRoute?.matched.map((m) => m.name);
      if (!matches || !matches.find((m) => m === "details-view")) {
        this.$router.push({
          name: "details-view",
          params: {
            ...this.$router.currentRoute.params,
          },
        });
      }
    }
  },
  computed: {
    shouldShowGetingImage() {
      return this.status !== "COMPLETE" || !this.monthsInitialised;
    },
    status() {
      return this.configuration?.status;
    },
    monthsInitialised() {
      return this.months.length > 0;
    },
  },
  methods: {
    navigateToListPage() {
      this.$router.push({
        name: "home",
      });
    },
    async getWorkloadTasks() {
      try {
        const result = await WorkloadTasks.getWorkloadTasks();
        this.workloadTasks = result.reduce((acc, task) => {
          acc.set(task.id, task);
          return acc;
        }, new Map());
      } catch (error) {
        this.workloadTasks = {};
      }
    },
    clearPollingInterval() {
      clearInterval(this.pollInterval);
    },

    // polls the given job with the passed id
    async poll(interval, job) {
      const complete = await job();

      if (!complete) {
        this.pollInterval = setInterval(() => job(), interval);
      }
    },
    selectTab(tab) {
      const matches = this.$router.currentRoute?.matched.map((m) => m.name);
      if (!this.$router.currentRoute || !matches.find((m) => m === tab.name)) {
        this.$router.push({
          name: tab.name,
          params: {
            ...this.$router.currentRoute.params,
          },
        });
      }
    },
    updateMonths(months) {
      this.months = this.fillUpEmptyMonths({
        configuration: this.configuration,
        months,
      });
    },
    fillUpEmptyMonths({ configuration, months = [] }) {
      let filledMonths = months;

      if (configuration) {
        let capacityPlanInterval = {
          start: utcToZonedTime(configuration.startDate, this.displayTimezone),
          end: utcToZonedTime(configuration.endDate, this.displayTimezone),
        };
        let intervalMonths = eachMonthOfInterval(capacityPlanInterval);
        let timezonedMonths = intervalMonths.map((m) =>
          zonedTimeToUtc(m, this.displayTimezone)
        );
        filledMonths = timezonedMonths.map((month, index) => {
          const filledMonth = months.find(
            (r) => r.date && new Date(r.date).getTime() === month?.getTime()
          ) || {
            date: month,
            isEmpty: true,
          };

          filledMonth["headcount"] = 0;

          if (
            !filledMonth["isEmpty"] &&
            filledMonth["isEmpty"] != true &&
            configuration["headCounts"] &&
            configuration["headCounts"].length > 0
          ) {
            for (let i = 0; i < configuration["headCounts"].length; i++) {
              if (
                isSameMonth(
                  intervalMonths[index],
                  parseISO(configuration["headCounts"][i]["month"])
                )
              ) {
                filledMonth["headcount"] =
                  configuration["headCounts"][i]["headCount"];
                break;
              }
            }
          }

          return filledMonth;
        });
      }

      return filledMonths;
    },
    async retrieveMonthsAndWarnings(id, callback) {
      const capacityPlanInterval = {
        start: utcToZonedTime(
          this.configuration.startDate,
          this.displayTimezone
        ),
        end: utcToZonedTime(this.configuration.endDate, this.displayTimezone),
      };

      const intervalMonths = eachMonthOfInterval(capacityPlanInterval);

      const intervalsPromisesArr = [];

      for (let x = 0; x < intervalMonths.length; x++) {
        const startDate = intervalMonths[x];

        const startDateInCorrectTz = zonedTimeToUtc(
          startDate,
          this.displayTimezone
        );

        intervalsPromisesArr.push(
          CapacityPlannerMonthlyView.getConfigurationByMonth(
            id,
            startDateInCorrectTz
          )
        );
      }

      const intervalMonthsData = await Promise.all(intervalsPromisesArr);

      const intervals = intervalMonthsData.map(
        (intervalMonth) => intervalMonth[0]
      );

      const warnings =
        await CapacityPlannerWarning.getWarningsForConfigurationId(id);

      this.warnings = warnings;
      this.checkWarnings({ warnings });
      this.updateMonths(intervals);
      if (callback) {
        callback();
      }
    },
    // Poll for when we have requirements but not availabilities yet
    async pollingAvailable(id) {
      const config = await this.getConfiguration(id);

      if (config.status === "COMPLETE") {
        this.clearPollingInterval();
        await this.retrieveMonthsAndWarnings(id);
        return true;
      }
      // If The status continuously getting "CALCULATING_AVAILABLE" STOP retry logic afetr MAX_RETRY_CALL exceeded.
      this.pollingAvailableStopCount++;
      if (this.pollingAvailableStopCount > MAX_RETRY_CALL) {
        this.clearPollingInterval();
        this.isErrorModal = true;
      }
      return false;
    },

    // Poll for when we do not have requirements yet
    async pollingRequired(id) {
      try {
        const config = await this.getConfiguration(id);

        // this status failed when calculation not complete with in 30 min.
        if (config.status === "FAILED") {
          await this.retrieveMonthsAndWarnings(id);
          this.isErrorModal = true;
          return true;
        }

        if (["CALCULATING_AVAILABLE", "COMPLETE"].indexOf(config.status) >= 0) {
          this.clearPollingInterval();
          await this.retrieveMonthsAndWarnings(id);

          // Months are retrieved so now poll if available is not yet calculated
          if (this.status !== "COMPLETE") {
            this.poll(MONTHLY_SUMMARY_POLLING_INTERVAL_AVAILABLE, () =>
              this.pollingAvailable(id)
            );
          }
          return true;
        }
        // If The status continuously getting "CREATE_CONFIGURATION" STOP retry logic afetr MAX_RETRY_CALL exceeded.
        this.pollingRequiredStopCount++;
        if (this.pollingRequiredStopCount > MAX_RETRY_CALL) {
          this.clearPollingInterval();
          this.isErrorModal = true;
        }

        return false;
      } catch (error) {
        // clear poll interval on error.
        this.clearPollingInterval();
        error.status === HTTP_STATUS_403
          ? (this.showNotFoundPage = true)
          : (this.isErrorModal = true);
        return true;
      }
    },

    async retrieveMonthlySummary(configurationId) {
      this.poll(MONTHLY_SUMMARY_POLLING_INTERVAL_REQUIRED, () =>
        this.pollingRequired(configurationId)
      );
    },
    featureEnabled(key) {
      return window.PlayvoxWFM.featureflow.evaluate(key).isOn();
    },
    onChange(data) {
      this.$emit("change", data);
    },
    async getConfiguration(id) {
      const configuration = await CapacityPlannerConfiguration.getConfiguration(
        id,
        true
      );
      this.configuration = configuration;
      this.displayTimezone = configuration.timezone;
      this.addConfigurationToRoute(configuration);
      return configuration;
    },
    addConfigurationToRoute(configuration) {
      this.$route.params.configuration = configuration;
    },
    editConfiguration() {
      let configurationId = this?.$route?.params?.id;
      this.$router.push(`/edit-configuration/${configurationId}`);
    },
    async getRosterConfigurations() {
      return RosterConfiguration.getRosterConfigurations({
        fields: "_id,name",
      });
    },
    async getForecasts() {
      return Forecast.getForecasts();
    },
    hoursOrFte(month) {
      if (!month) {
        return 0;
      }
      let { hours, fte } = month;
      return (this.configuration.unitOfWork !== "FTE" ? hours : fte) || 0;
    },
    async downloadMonthlyCsv() {
      if (
        this.status === "COMPLETE" ||
        !this.loading ||
        !this.shouldShowGetingImage
      ) {
        this.generatingCsv = true;
        [this.forecastList, this.rosterConfigurationList] = await Promise.all([
          this.getForecasts(),
          this.getRosterConfigurations(),
        ]);

        let configuration = this.configuration;
        let months = this.months;
        let forecast = this.forecastList.find(
          (f) => f.id == configuration.forecastId
        );
        let rosterConfigurationListInConfig =
          this.rosterConfigurationList.filter((r) =>
            configuration.rosterConfigurationId.find((p) => r.id === p._id)
          );

        //Map all the csv data
        const headerRow = months.map(
          (month) =>
            (month.date.getMonth() < 9
              ? "0" + (month.date.getMonth() + 1)
              : month.date.getMonth() + 1) +
            "/" +
            month.date.getFullYear()
        );
        headerRow.unshift("");

        const requiredRow = months.map((month) =>
          formatNumberForCsv(this.hoursOrFte(month.required))
        );
        requiredRow.unshift(
          this.translate("monthly-csv-required-capacity-rowheader")
        );

        months = months.map((month) => ({
          ...month,
          calculatedAvailable:
            this.hoursOrFte(month.available) -
            (this.hoursOrFte(month.unplannedLeave) +
              this.hoursOrFte(month.plannedLeave)) -
            this.hoursOrFte(month.shrinkage),
        }));

        const availableRow = months.map((month) =>
          formatNumberForCsv(month.calculatedAvailable)
        );
        availableRow.unshift(
          this.translate("monthly-csv-available-capacity-rowheader")
        );

        const shrinkageRow = months.map((month) =>
          formatNumberForCsv(this.hoursOrFte(month.shrinkage))
        );
        shrinkageRow.unshift(this.translate("monthly-csv-shrinkage-rowheader"));

        const leaveRow = months.map((month) =>
          formatNumberForCsv(
            this.hoursOrFte(month.unplannedLeave) +
              this.hoursOrFte(month.plannedLeave)
          )
        );
        leaveRow.unshift(this.translate("monthly-csv-leave-rowheader"));

        const varianceRow = months.map((month) =>
          formatNumberForCsv(
            month.calculatedAvailable - this.hoursOrFte(month.required)
          )
        );
        varianceRow.unshift(this.translate("monthly-csv-variance-rowheader"));

        const blankRow = [];

        const forecastRow = [
          this.translate("monthly-csv-forecast-rowheader"),
          forecast.name,
        ];

        const timezoneRow = [
          this.translate("monthly-csv-timezone-rowheader"),
          configuration.timezone,
        ];

        const rostersRow = [
          this.translate("monthly-csv-rosters-rowheader"),
          rosterConfigurationListInConfig.map((r) => r.name).join(","),
        ];

        const plannedLeaveRow = [
          this.translate("monthly-csv-planned-leave-percentage-rowheader"),
          configuration.defaultPlannedLeaveTargetPercentage,
        ];

        const unplannedLeaveRow = [
          this.translate("monthly-csv-unplanned-leave-percentage-rowheader"),
          configuration.defaultUnplannedLeaveTargetPecentage,
        ];

        const shrinkagePerMonthRow = [
          this.translate("monthly-csv-shrinkage-percentage-rowheader"),
          configuration.defaultShrinkageTargetPercentage,
        ];

        const attritionRow = [
          this.translate("monthly-csv-attrition-percentage-rowheader"),
          configuration.attritionTargetPercentage,
        ];

        const unitOfWorkRow = [
          this.translate("monthly-csv-unitofwork-rowheader"),
          configuration.unitOfWork,
        ];

        const nestTime = [
          this.translate("monthly-csv-nest-time-rowheader"),
          configuration.nestTime,
        ];

        const csvData = [
          headerRow,
          requiredRow,
          availableRow,
          shrinkageRow,
          leaveRow,
          varianceRow,
          blankRow,
          forecastRow,
          timezoneRow,
          rostersRow,
          plannedLeaveRow,
          unplannedLeaveRow,
          shrinkagePerMonthRow,
          attritionRow,
          unitOfWorkRow,
          nestTime,
        ];

        //Helpful for debugging
        //console.log(csvData.map(function(d){ return d.join(); }).join('\n'));

        //Windows doesn't like some of the characters used in plan names, we'll just filter those for now
        let filename =
          configuration.name.replace(/[/\\?%*:|"<>]/g, "-") + ".csv";
        exportToCsv(filename, csvData);

        this.generatingCsv = false;
        return { filename, csvData };
      }
    },
    checkWarnings({ warnings = [] }) {
      // * No Forecast Requirements
      this.showRequiredUnavailable =
        CapacityPlannerWarning.getIdentifierCodeIndex({
          warnings,
          identifierCode: "NO_FORECAST_REQUIREMENTS",
        }) >= 0;

      if (
        this.showRequiredUnavailable &&
        !this.disableOpenNoForecastRequirementWarningModal
      ) {
        // * only open modal once
        this.openNoForecastRequirementWarningModal();
      }

      this.showNoAgentGotShift =
        CapacityPlannerWarning.getIdentifierCodeIndex({
          warnings,
          identifierCode: "NO_AGENTS_GOT_SHIFTS",
        }) >= 0;
      if (this.showNoAgentGotShift && !this.disableOpenNoAgentGotShiftModal) {
        // * only open modal once
        this.openNoAgentGotShiftWarningModal();
      }
    },
    openNoForecastRequirementWarningModal() {
      this.noForecastRequirementWarningModalConfig = {
        header: this.translate(
          "monthly-summary-no-forecast-requirements-heading"
        ),
        buttons: [
          {
            class: "is-secondary is-small",
            label: this.translate("continue"),
            testId: "accept",
            command: () => {
              this.closeNoForecastRequirementWarningModal();
            },
          },
        ],
      };
      this.noForecastRequirementWarningModalActive = true;
      this.disableOpenNoForecastRequirementWarningModal = true; // * only open modal once
    },
    closeNoForecastRequirementWarningModal() {
      this.noForecastRequirementWarningModalActive = false;
    },
    openNoAgentGotShiftWarningModal() {
      this.noAgentGotShiftWarningModalConfig = {
        header: this.translate("monthly-summary-no-agent-got-shift-heading"),
        buttons: [
          {
            class: "is-secondary is-small",
            label: this.translate("continue"),
            testId: "accept",
            command: () => {
              this.closeNoAgentGotShiftWarningModal();
            },
          },
        ],
      };
      this.noAgentGotShiftWarningModalActive = true;
      this.disableOpenNoAgentGotShiftWarningModalActive = true; // * only open modal once
    },
    closeNoAgentGotShiftWarningModal() {
      this.noAgentGotShiftWarningModalActive = false;
    },
  },
};
</script>

<style lang="scss">
#details-tabs > .dr-tabs {
  margin-bottom: 1em;
}

.dr-date-picker {
  min-width: 10em;
  max-width: 15em;
}
</style>

<style lang="scss" scoped>
.page-header {
  display: flex;
  flex-direction: row;
  align-self: flex-start;
  align-items: center;
  margin-bottom: 1.5rem;
  width: 100%;

  & .page-title {
    margin-bottom: 0;
  }
}

.page-header-actions {
  margin-left: auto;
  display: flex;

  & > button svg {
    padding-top: 8px;
    padding-bottom: 4px;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
}
.page-header-actions > button {
  height: 40px;
}
.page-header-actions svg {
  width: 2.75rem;
  height: 2.75rem;
}
.page-header-actions .dr-icon {
  font-size: 2.5rem;
}

@media (max-width: 599px) {
  .page-header-actions > button {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
}
.img-wrapper {
  display: flex;
  flex-direction: column;
  margin-bottom: 5em;

  & > * {
    max-width: 90vw;
    max-height: 65vh;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
